// Hooks
import { useStyles } from './styles/Users.styles';
import { useDeferredValue, useMemo } from 'react';
import { useListUsersQuery } from '@features/redux/apis/auth';
import { useNameMapQuery } from '@features/redux/apis/org';
import { useAlert } from '@hooks/useAlert';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useSelector } from 'react-redux';

// Base Components
import { ScrollToTop } from '@components/ScrollToTop';
import { DataTable, type ColumnOptions } from '@components/tables/DataTable';
import { QueryEffect } from '@helpers/QueryEffect';

// Fixed Scope Components
import { UsersHeader } from './UsersHeader';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { Chips, chipCell } from './ChipCell';
import { PERMISSIONS } from '@consts/user';
import { UserActions } from './UserActions';

// Types
import type { User } from '@features/redux/apis/auth';
import type { RootState } from '@features/redux/store';

const PAGE_SIZE = 60;

export const Users = () => {
    const classes = useStyles();
    const { setAlert } = useAlert();

    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const { data: { companies = {}, resellers = {}, locations = {} } = {} } = useNameMapQuery({});

    const infoState = useLocalStorage({
        key: 'user-search-info',
        value: { search: '', page: 1 },
    });
    const [searchInfo, setSearchInfo] = infoState;

    const queryState = useListUsersQuery({
        search: useDeferredValue(searchInfo.search),
        page: useDeferredValue(searchInfo.page),
        limit: PAGE_SIZE,
    });
    const {
        data = {
            data: [],
            total: 0,
            limit: 60,
            page: {
                curr: 1,
                next: null,
                prev: null,
            },
        },
        isLoading,
    } = queryState;

    const { data: users, total, limit } = data;

    const columns = useMemo<ColumnOptions<User>[]>(() => {
        return [
            {
                headerName: 'First Name',
                field: 'given_name',
                minWidth: 100,
                maxWidth: 200,
            },
            {
                headerName: 'Last Name',
                field: 'family_name',
                minWidth: 100,
                maxWidth: 200,
            },
            {
                headerName: 'Email',
                field: 'email',
                minWidth: 200,
                maxWidth: 350,
            },
            {
                headerName: 'Companies',
                field: 'custom:Companies',
                renderCell: chipCell('custom:Companies', companies),
                minWidth: 150,
            },
            {
                headerName: 'Resellers',
                field: 'custom:Resellers',
                renderCell: chipCell('custom:Resellers', resellers),
                minWidth: 150,
            },
            {
                headerName: 'Locations',
                field: 'custom:Locations',
                renderCell: chipCell('custom:Locations', locations),
                minWidth: 150,
            },
            {
                headerName: 'Permissions',
                field: 'custom:userType',
                ignoreValue: true,
                minWidth: 150,
                renderExtraCellContent: (row) => (
                    <Chips
                        values={PERMISSIONS.reduce((acc, { key, label }) => {
                            if (+row[key] > 0) acc.push(label);
                            return acc;
                        }, [] as string[])}
                    />
                ),
            },
            ...((role.isAdmin
                ? [
                      {
                          headerName: 'Notes (Internal)',
                          field: 'custom:Notes',
                          minWidth: 125,
                      },
                  ]
                : []) satisfies ColumnOptions<User>[]),
            {
                headerName: 'Actions',
                field: 'sub',
                renderCell: ({ row }) => {
                    return <UserActions user={row} />;
                },
                width: 75,
            },
        ];
    }, [companies, resellers, locations]);

    return (
        <div className={classes.root}>
            {/* Separating this into <UsersEffects> would require passing
                state into the effects component (the query is dependent on state)
            */}
            <QueryEffect
                queryState={queryState}
                effects={{
                    isError: () => {
                        setAlert('Failed to fetch users. Please reload and try again.', 'error');
                    },
                }}
            />

            <ScrollToTop />
            <BreadcrumbSubBar />

            <UsersHeader infoState={infoState} />

            <DataTable
                storageKey='Users'
                height={'calc(100dvh - 320px)'}
                // showLastButton={false}
                // showBackButton={false}
                virtualize={false}
                loading={isLoading}
                rows={users}
                columns={columns}
                pinnedColumns={{
                    right: ['sub'],
                    left: ['custom:Notes'],
                }}
                density='standard'
                pageSize={limit}
                getUniqueRowId={({ sub, Username }) => `user-${Username}-${sub}`}
                page={searchInfo.page}
                totalRowCount={total}
                pagination
                paginationMode='server'
                getRowHeight={() => 'auto'}
                onPaginate={(next: number) => {
                    setSearchInfo((prev) => ({
                        ...prev,
                        page: next,
                    }));
                }}
            />
        </div>
    );
};
